class BlogSearch extends HTMLElement {
  constructor() {
    super();
    this.searchAbortController = null;
    this.currentSearchTerm = null;
    this.debounceTimer = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const searchComponent = this;
    const form = searchComponent.querySelector(".js-blog-search-form");
    const input = searchComponent.querySelector(".js-blog-search-input");
    const resultsWrapper = searchComponent.querySelector(".js-blog-search-results");
    const noResultsText = "No articles found";
    const searchingClass = "is-searching";

    const blogList = document.querySelector(".blg-Blog .art-List_Items");
    const loadMoreContainer = document.querySelector(".blg-LoadMore");

    const getArticleCards = () => {
      if (!blogList) return [];
      return Array.from(blogList.querySelectorAll("[data-blog-article]"));
    };

    if (loadMoreContainer) loadMoreContainer.style.display = "";

    const escapeHtml = (string) => {
      if (!string) return "";
      return string.replace(/[&<>"']/g, (match) => {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[match];
      });
    };

    const hideResults = () => {
      searchComponent.classList.remove("is-open");
      if (resultsWrapper) resultsWrapper.innerHTML = "";
    };

    const showResultMessage = (text) => {
      if (!resultsWrapper) return;
      resultsWrapper.innerHTML =
        '<div class="blg-Navigation_Empty">' + escapeHtml(text) + "</div>";
      searchComponent.classList.add("is-open");
    };

    const renderAdditionalMatches = () => {
      if (!resultsWrapper) return;
      let oldExtra = resultsWrapper.querySelector(".blg-Navigation_ExtraResults");
      if (oldExtra) oldExtra.remove();
      searchComponent.classList.add("is-open");
    };

    const loadAllArticles = (term, callback) => {
      if (!term || term.trim().length < 3) return callback([]);

      const blogUrl = searchComponent.dataset.blogUrl || "";
      if (!blogUrl) return callback([]);

      this.currentSearchTerm = term.trim();

      if (this.searchAbortController) {
        this.searchAbortController.abort();
      }

      this.searchAbortController = new AbortController();
      const localAbort = this.searchAbortController;

      const url =
        blogUrl.replace(/\/$/, "") +
        "?view=search-endpoint&q=" +
        encodeURIComponent(term) +
        "&limit=1000";

      fetch(url, {
        signal: this.searchAbortController.signal,
        headers: { Accept: "application/json, */*" },
      })
        .then((r) => {
          if (localAbort.signal.aborted) return { articles: [] };
          if (!r.ok) return { articles: [] };
          return r.json().catch(() => ({ articles: [] }));
        })
        .then((data) => {
          if (localAbort.signal.aborted) return callback([]);
          callback(data.articles || []);
        })
        .catch((e) => {
          if (e.name === "AbortError") return callback([]);
          callback([]);
        });
    };

    const filterArticles = (term) => {
      const q = term.toLowerCase().trim();

      if (!q || q.length < 3) {
        if (blogList) {
          getArticleCards().forEach((card) => (card.style.display = ""));
          blogList.classList.remove(searchingClass);
        }
        hideResults();
        return;
      }

      if (blogList) blogList.classList.add(searchingClass);

      // Local filtering
      const cards = getArticleCards();
      let visibleCount = 0;
      let visibleSet = new Set();

      cards.forEach((card) => {
        const title = card.dataset.articleTitle || "";
        const tags = card.dataset.articleTags || "";
        const phrase = q;

        let match =
          title.toLowerCase().includes(phrase) ||
          tags.toLowerCase().includes(phrase);

        card.style.display = match ? "" : "none";

        if (match) {
          visibleCount++;
          visibleSet.add(title.toLowerCase());
        }
      });

      if (resultsWrapper) {
        if (visibleCount > 0) {
          showResultMessage(`Showing ${visibleCount} article${visibleCount === 1 ? "" : "s"}`);
        } else {
          showResultMessage("Searching…");
        }
      }

      // Search all articles from server
      loadAllArticles(term, (all) => {
        let extraMatches = 0;

        all.forEach((a) => {
          let t = (a.title || "").toLowerCase();
          let p = q;
          let matched = false;

          if (t.includes(p)) matched = true;
          if (!matched && Array.isArray(a.tags)) {
            a.tags.some((tag) => {
              if (String(tag).toLowerCase().includes(p)) {
                matched = true;
                return true;
              }
            });
          }

          if (matched && !visibleSet.has(t)) {
            extraMatches++;
          }
        });

        let total = visibleCount + extraMatches;

        if (resultsWrapper) {
          if (total > 0) {
            showResultMessage(
              `Showing ${total} article${total === 1 ? "" : "s"}`
            );
          } else {
            showResultMessage(noResultsText + ' "' + term + '"');
          }
        }

        renderAdditionalMatches();
      });
    };

    if (!form || !input) return;

    input.addEventListener("input", () => {
      const term = input.value.trim();
      clearTimeout(this.debounceTimer);

      if (!term || term.length < 3) {
        hideResults();
        getArticleCards().forEach((card) => (card.style.display = ""));
        if (blogList) blogList.classList.remove(searchingClass);
        return;
      }

      this.debounceTimer = setTimeout(() => filterArticles(term), 300);
    });

    input.addEventListener("focus", () => {
      const term = input.value.trim();
      if (term.length >= 3) filterArticles(term);
    });

    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        hideResults();
        input.blur();
      }
    });

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const term = input.value.trim();
      if (term.length >= 3) filterArticles(term);
    });

    document.addEventListener("click", (e) => {
      if (!searchComponent.contains(e.target)) hideResults();
    });
  }
}

customElements.define("blog-search", BlogSearch);